import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ShieldCheck, Loader2 } from 'lucide-react'
import { useAuthStore } from '../../../stores/authStore.js'
import { loginUser, getCurrentUser } from '../../../services/api.js'
import { isAdmin } from '../utils/isAdmin.js'

/**
 * Page de connexion admin discrète (URL non liée depuis le reste du site).
 *
 * Flux : /api/login (email + mot de passe) → on pose le token → /api/auth/me
 * pour récupérer le rôle → si ROLE_ADMIN, accès à l'espace admin ; sinon on
 * déconnecte et on refuse. La frontière réelle reste le ROLE_ADMIN côté API.
 */
export default function AdminLoginPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const setToken = useAuthStore((s) => s.setToken)
  const setUser = useAuthStore((s) => s.setUser)
  const logout = useAuthStore((s) => s.logout)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Déjà connecté en admin : on file directement à l'espace admin.
  if (isAuthenticated && isAdmin(user)) {
    return <Navigate to="/dashboard/compte/admin" replace />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const { token } = await loginUser({ email: email.trim(), password })
      if (!token) throw new Error('Réponse de connexion invalide')

      // On pose le token pour que /api/auth/me soit authentifié…
      setToken(token)
      const me = await getCurrentUser()

      // …puis on n'autorise que les administrateurs.
      if (!isAdmin(me)) {
        logout()
        setError('Accès réservé aux administrateurs.')
        return
      }

      setUser(me)
      navigate('/dashboard/compte/admin', { replace: true })
    } catch (err) {
      logout()
      const status = err?.response?.status
      setError(status === 401 ? 'Identifiants incorrects.' : (err?.message || 'Connexion impossible.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-sm rounded-2xl border border-ink/10 bg-ink/[0.03] p-8">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-[#5b5cf4]/30 bg-[#5b5cf4]/10">
            <ShieldCheck className="h-6 w-6 text-[#5b5cf4]" />
          </div>
          <h1 className="text-lg font-bold text-ink">Administration</h1>
          <p className="mt-1 text-sm text-ink/40">Accès réservé aux administrateurs.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs text-ink/45">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="username"
              className="w-full rounded-xl border border-ink/10 bg-shade/30 px-3 py-2 text-sm text-ink placeholder:text-ink/25 focus:border-[#5b5cf4]/40 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-ink/45">Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full rounded-xl border border-ink/10 bg-shade/30 px-3 py-2 text-sm text-ink placeholder:text-ink/25 focus:border-[#5b5cf4]/40 focus:outline-none"
            />
          </div>

          {error && (
            <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#5b5cf4]/15 px-4 py-2.5 text-sm font-semibold text-[#5b5cf4] transition hover:bg-[#5b5cf4]/25 disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Se connecter
          </button>
        </form>
      </div>
    </div>
  )
}
